import { setEnhancedConversionData, trackAdsConversion, trackEvent } from './analytics';

export type CtaLocation = 'hero' | 'sticky' | 'final' | 'fee_section';
export type FormErrorReason = 'validation' | 'server' | 'network' | 'rate_limit';

/**
 * What a lead may say about itself in analytics.
 *
 * Only values chosen from a fixed list, and a count. Never a name, an email, a
 * company or anything typed freely: GA4's terms forbid personal data in event
 * parameters, and a free-text field can contain anything. `email` is the one
 * exception and it never becomes a parameter -- it is handed to gtag's
 * `user_data` for enhanced conversions, which hashes it before it leaves the
 * browser, and only when the build and the visitor's consent both allow it.
 */
export type LeadDetails = {
  /** Which of the two employer forms sent it. */
  formId: 'qa_talent_form' | 'qa_talent_detailed_form';
  leadSource: 'qa_talent' | 'hire_form';
  roleType?: string;
  seniority?: string;
  employmentType?: string;
  headcount?: number;
  email?: string;
};

/**
 * Every QA Talent event in one place, so the names cannot drift between the page,
 * the sticky bar and the forms.
 *
 * `qa_talent_lead` is THE employer-lead event. The Google Ads conversion that
 * counts it -- the one Primary conversion -- is sent at the same moment by
 * `trackAdsConversion`, keyed by the lead id; GA4 keeps `qa_talent_lead` for
 * reporting and it is not imported into Ads. Both employer forms send it --
 * the short form on /for-employers and the detailed one on /hire-qa-talent --
 * told apart by `form_id`, so a visitor who follows the "send a full brief" link
 * from an ad still counts. `generate_lead` also fires, because it is GA4's
 * recommended event name; since the candidate application was renamed to
 * `candidate_application_submit` it means "a commercial enquiry" (these two
 * forms plus the consulting brief on the home page). It is still not the Ads
 * conversion: see docs/analytics-audit.md.
 */
export const qaTalentEvents = {
  pageView: () => trackEvent('qa_talent_page_view'),
  ctaClick: (location: CtaLocation) => trackEvent('qa_talent_primary_cta_click', { location }),
  scheduleCallClick: () => trackEvent('qa_talent_schedule_call_click'),
  emailClick: () => trackEvent('qa_talent_email_click'),
  formStart: () => trackEvent('qa_talent_form_start'),
  splashView: () => trackEvent('qa_talent_splash_view'),
  splashCtaClick: () => trackEvent('qa_talent_splash_cta_click'),
  splashDismiss: () => trackEvent('qa_talent_splash_dismiss'),
  formSubmit: () => trackEvent('qa_talent_form_submit'),
  formError: (reason: FormErrorReason) => trackEvent('qa_talent_form_error', { reason }),

  /**
   * The server recognised a repeat of an enquiry it already has and returned
   * the original id. Not a lead and never a conversion -- no `qa_talent_lead`,
   * no `generate_lead`, no Ads conversion -- so GA4's lead counts stay true.
   * Recorded only so the rate of repeats can be seen.
   */
  duplicateSubmit: (formId: LeadDetails['formId']) =>
    trackEvent('qa_talent_form_duplicate', { form_id: formId }),

  /**
   * Only ever called after the server has accepted the enquiry.
   *
   * `submissionId` is the server's own id for the record. It becomes the Ads
   * conversion's `transaction_id`, so a lead cannot be counted twice, and
   * `lead_id` on the GA4 event, so a later offline upload can match a qualified
   * lead back to the click that produced it.
   */
  leadSuccess: (submissionId: string, details: LeadDetails) => {
    // Before any conversion is sent, so the hashed address travels with it.
    if (details.email) setEnhancedConversionData(details.email);

    const params: Record<string, unknown> = {
      form_id: details.formId,
      lead_source: details.leadSource,
    };
    if (submissionId) params.lead_id = submissionId;
    if (details.roleType) params.role_type = details.roleType;
    if (details.seniority) params.seniority = details.seniority;
    if (details.employmentType) params.employment_type = details.employmentType;
    if (details.headcount) params.headcount = details.headcount;

    trackEvent('qa_talent_form_success', { form_id: details.formId });
    trackEvent('qa_talent_lead', params);
    trackEvent('generate_lead', {
      form_name: details.formId === 'qa_talent_form' ? 'qa_talent' : 'hire_qa_talent',
      lead_source: details.leadSource,
    });
    trackAdsConversion(submissionId);
  },
};
